// src/api/service.network.ts
import { http } from './http.ts';
import { ENDPOINTS } from './endpoints.ts';

export type SearchFilterOption = {
  searchKeyword: string;
  filterMinPrice: number;
  filterMaxPrice: number;
  filterRating: number | null;

  page: number;
  perPage: number;

  selectMainCategories: Array<number | string>;
  selectSubCategories: Array<number | string>;
  selectLocationTags: Array<number | string>;
};

type QueryParams = Record<string, string | number | boolean>;

function addListParam(query: QueryParams, key: string, list: Array<string | number>) {
  list.forEach((v, i) => {
    query[`${key}[${i}]`] = v;
  });
}

export type ServiceInquiryInput = {
  name: string;
  email: string;
  contact?: string;
  travel_date?: string;
  pax?: number;
  message: string;
  locale?: string;
};

export const ServiceNetwork = {
  postInquiry: (uuid: string, body: ServiceInquiryInput) =>
    http.post(ENDPOINTS.service.serviceInquiries(uuid), body),

  getDashboard: (currency?: string) =>
    http.get(ENDPOINTS.dashboard.dashboard, currency ? { params: { currency } } : undefined),

  getCategory: (categoryId: string, currency?: string) =>
    http.get(ENDPOINTS.service.category(categoryId), currency ? { params: { currency } } : undefined),

  getServiceList: (searchOption: SearchFilterOption, currency?: string) => {
    const query: QueryParams = {
      page: searchOption.page,
      per_page: searchOption.perPage,
    };

    if (searchOption.searchKeyword?.trim()) query.keyword = searchOption.searchKeyword.trim();
    if (searchOption.filterMinPrice !== 0) query.min_price = searchOption.filterMinPrice;
    if (searchOption.filterMaxPrice !== 0) query.max_price = searchOption.filterMaxPrice;
    if (searchOption.filterRating != null) query.rating = searchOption.filterRating;
    if (currency) query.currency = currency;

    addListParam(query, 'category_ids', searchOption.selectMainCategories);
    addListParam(query, 'sub_category_ids', searchOption.selectSubCategories);
    addListParam(query, 'location_tags', searchOption.selectLocationTags);

    return http.get(ENDPOINTS.service.list, { params: query });
  },

  getServiceDetail: (serviceUUID: string, currency?: string) =>
    http.get(ENDPOINTS.service.detail(serviceUUID), currency ? { params: { currency } } : undefined),

  // Fire-and-forget engagement ping (스크롤 깊이/체류) — Phase C. device='web' is
  // derived server-side from the /web mount; never surface failures.
  postEngagement: (serviceUUID: string, maxDepth: number, dwellMs: number) =>
    http
      .post(ENDPOINTS.service.engagement(serviceUUID), { max_depth: maxDepth, dwell_ms: dwellMs })
      .catch(() => {}),

  getSearchHome: () =>
    http.get(ENDPOINTS.service.searchHome),

  getSearchSuggestions: (keyword: string) =>
    http.get(ENDPOINTS.service.searchSuggest, { params: { keyword } }),

  getServiceBookingInfo: (uuid: string, currency?: string) =>
    http.get(ENDPOINTS.service.serviceBookingInfo(uuid), currency ? { params: { currency } } : undefined),

  // 그룹(합류형) 출발 목록 — booking_mode + 날짜별 booked/remaining
  getServiceDepartures: (uuid: string, from?: string, to?: string) =>
    http.get(ENDPOINTS.service.serviceDepartures(uuid), {
      params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    }),

  getProviderProfile: (uuid: string, currency?: string) =>
    http.get(ENDPOINTS.service.providerProfile(uuid), currency ? { params: { currency } } : undefined),

  deleteSearchHistories: (keywords?: string[]) => {
    if (!keywords || keywords.length === 0) {
      return http.delete(ENDPOINTS.service.searchHistories);
    }
    const params: QueryParams = {};
    addListParam(params, 'keywords', keywords);
    return http.delete(ENDPOINTS.service.searchHistories, { params });
  },
};